import classnames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import useCashbackProgram from '../../hooks/cashback/useCashbackProgram';
import { ENVIRONMENTS } from '../../services/exports/Constants';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../store/types';
import HelperMethods from '../../services/exports/HelperMethods';

interface Props {
  className?: string;
}

export default function LoginBanner(props: Props) {
  const { className } = props;

  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:LoginBanner' });
  const { toggleLoginModal } = useAppMethods();
  const { update } = useOrderFlow();
  const { cashbackProgram, formatPointsAsMoney, freeRewardProduct } = useCashbackProgram();
  const currency = useSelector((state: StoreInterface) => state.initialData?.company?.currency);
  const numberLocale = useSelector((state: StoreInterface) => state.initialData?.company?.region?.number_locale);

  const maxSpendingLimit = cashbackProgram?.max_spending_limit;
  const hasMaxDiscount = !!maxSpendingLimit && maxSpendingLimit > 0;
  const maxDiscount = hasMaxDiscount ? formatPointsAsMoney(maxSpendingLimit) : null;
  const freeProductName = freeRewardProduct?.name ?? null;

  const fillDemoModeInfo = () =>
    update({
      customer_first_name: 'Max',
      customer_last_name: 'Mustermann',
      customer_email: 'max.musterman@demofoodamigos.io',
      customer_phone_number: '+491780000000',
    });

  const isDev = import.meta.env.VITE_MODE !== ENVIRONMENTS.PROD;

  const openLogin = () => toggleLoginModal();

  return (
    <div className={classnames(container, className)}>
      <div className="flex items-start gap-sm">
        <span className={iconStyle}>
          <span className="text-brand-primary text-lg font-semibold leading-none">
            {HelperMethods.getCurrencySymbol(currency, numberLocale)}
          </span>
        </span>
        <div className="flex-1 space-y-2xs">
          <p className="text-sm text-brand-text-primary">
            {t('have_account')}{' '}
            <button type="button" onClick={openLogin} className={linkStyle}>
              {t('sign_in')}
            </button>{' '}
            {t('sign_in_description')}
          </p>
          <p className="text-sm text-muted-foreground">
            <button type="button" onClick={openLogin} className={linkStyle}>
              {t('join_free')}
            </button>{' '}
            {hasMaxDiscount ? (
              <Trans
                t={t}
                values={{ max_discount: maxDiscount, product_name: freeProductName }}
                components={{
                  highlight: <span className="text-brand-primary font-semibold" />,
                }}
              >
                {freeProductName ? 'join_description_with_product_and_discount' : 'join_description_with_discount'}
              </Trans>
            ) : (
              t('join_description')
            )}
          </p>
        </div>
      </div>
      {isDev && (
        <button type="button" onClick={fillDemoModeInfo} className={demoButtonStyle}>
          {t('demo_mode')}
        </button>
      )}
    </div>
  );
}

const container = 'rounded-xl border-xs border-brand-primary/25 bg-brand-primary/10 px-md py-md';

const iconStyle = 'mt-nano h-9 w-9 shrink-0 rounded-full bg-brand-primary/20 flex items-center justify-center';

const linkStyle = 'text-brand-primary font-semibold hover:underline';

const demoButtonStyle =
  'mt-md w-full rounded-full border-xs border-brand-primary/40 bg-brand-card px-md py-sm text-sm font-semibold text-brand-primary hover:bg-brand-primary/10 transition-colors';
